import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle, XCircle, Save, RotateCcw, LoaderCircle, ShieldCheck,
  Settings2, Users, PiggyBank, HandCoins, Receipt, UserCheck,
  AlertTriangle, RefreshCw, ChevronDown, GripVertical, Sparkles, Power,
  ChevronsUpDown, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ToggleSwitch } from "@/components/ui/toggle-switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { Option } from "@/components/ui/multiselect";
import { PERMISSION_CATEGORIES } from "@/pages/roles-and-permissions/constants";
import { staggerContainer, staggerItem, hoverScale, tapScale } from "@/lib/animations";

const ENTITY_ICONS: Record<string, any> = {
  loan: HandCoins,
  declaration: PiggyBank,
  payment: Receipt,
  membership: UserCheck,
  expense: Receipt,
};

const ENTITY_LABELS: Record<string, string> = {
  loan: "Loan Approval",
  declaration: "Saving Declaration Approval",
  payment: "Member Payment Approval",
  membership: "Membership Approval",
  expense: "Expense Approval",
};

const ENTITY_DESCRIPTIONS: Record<string, string> = {
  loan: "Configure who can approve loan applications and how many approvals are required",
  declaration: "Configure who can verify member saving declarations",
  payment: "Configure who can verify member payments",
  membership: "Configure who can approve new member registrations",
  expense: "Configure who can approve expense requests",
};

const ENTITY_PERMISSION_MODULES: Record<string, string[]> = {
  loan: ["loans"],
  declaration: ["contributions"],
  payment: ["contributions"],
  membership: ["members"],
  expense: ["expenses"],
};

const ENTITY_ACCENTS: Record<string, string> = {
  loan: "from-amber-500/10 to-amber-600/5 border-amber-200/60",
  declaration: "from-blue-500/10 to-blue-600/5 border-blue-200/60",
  payment: "from-purple-500/10 to-purple-600/5 border-purple-200/60",
  membership: "from-emerald-500/10 to-emerald-600/5 border-emerald-200/60",
  expense: "from-rose-500/10 to-rose-600/5 border-rose-200/60",
};

const ENTITY_ICON_COLORS: Record<string, string> = {
  loan: "bg-amber-100 text-amber-700",
  declaration: "bg-blue-100 text-blue-700",
  payment: "bg-purple-100 text-purple-700",
  membership: "bg-emerald-100 text-emerald-700",
  expense: "bg-rose-100 text-rose-700",
};

function getPermissionOptions(entityType: string): Option[] {
  const moduleIds = ENTITY_PERMISSION_MODULES[entityType] || [];
  const options: Option[] = [];
  for (const category of PERMISSION_CATEGORIES) {
    for (const mod of category.modules) {
      if (moduleIds.includes(mod.id)) {
        for (const action of mod.actions) {
          options.push({
            value: `${mod.id}.${action.id}`,
            label: `${mod.label} — ${action.label}`,
          });
        }
      }
    }
  }
  return options;
}

function PermissionMultiSelect({
  entityType,
  value,
  onChange,
}: {
  entityType: string;
  value: string[];
  onChange: (keys: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const allOptions = useMemo(() => getPermissionOptions(entityType), [entityType]);

  const selectedOptions = allOptions.filter((o) => value.includes(o.value));

  const filtered = useMemo(
    () => allOptions.filter((o) => o.label.toLowerCase().includes(search.toLowerCase())),
    [allOptions, search]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="h-auto min-h-[36px] w-full justify-between text-xs font-normal"
        >
          {selectedOptions.length === 0
            ? "Select permissions..."
            : `${selectedOptions.length} permission${selectedOptions.length > 1 ? "s" : ""} selected`
          }
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start" sideOffset={4}>
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 shrink-0 opacity-50" />
          <input
            placeholder="Search permissions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>
        <div className="max-h-[260px] overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">No permissions found</p>
          ) : (
            filtered.map((opt) => {
              const isSelected = value.includes(opt.value);
              return (
                <div
                  key={opt.value}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(
                      isSelected
                        ? value.filter((v) => v !== opt.value)
                        : [...value, opt.value]
                    );
                  }}
                  className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
                >
                  <Checkbox checked={isSelected} className="h-4 w-4" />
                  <span className="text-xs">{opt.label}</span>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RoleMultiSelect({
  roles,
  value,
  onChange,
}: {
  roles: { id: number; name: string }[];
  value: number[];
  onChange: (ids: number[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedRoles = roles.filter((r) => value.includes(r.id));

  const filtered = useMemo(
    () => roles.filter((r) => r.name.toLowerCase().includes(search.toLowerCase())),
    [roles, search]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="h-auto min-h-[36px] w-full justify-between text-xs font-normal"
        >
          {selectedRoles.length === 0
            ? "Select designated roles..."
            : `${selectedRoles.length} role${selectedRoles.length > 1 ? "s" : ""} selected`
          }
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start" sideOffset={4}>
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 shrink-0 opacity-50" />
          <input
            placeholder="Search roles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>
        <div className="max-h-[260px] overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">No roles found</p>
          ) : (
            filtered.map((role) => {
              const isSelected = value.includes(role.id);
              return (
                <div
                  key={role.id}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(
                      isSelected
                        ? value.filter((id) => id !== role.id)
                        : [...value, role.id]
                    );
                  }}
                  className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
                >
                  <Checkbox checked={isSelected} className="h-4 w-4" />
                  <span className="text-xs font-medium">{role.name}</span>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface Workflow {
  id: number;
  entityType: string;
  requiredApprovals: number;
  approverPermissionKeys: string[];
  designatedRoleIds: number[];
  allowAdminOverride: boolean;
  requireComments: boolean;
  label: string;
  enabled: boolean;
  sortOrder: number;
}

function WorkflowCard({
  wf,
  editing,
  hasChanges,
  local,
  onFieldChange,
  onSave,
  isSaving,
  roles,
}: {
  wf: Workflow;
  editing: Partial<Workflow> | undefined;
  hasChanges: boolean;
  local: Workflow;
  onFieldChange: (field: string, value: any) => void;
  onSave: () => void;
  isSaving: boolean;
  roles: { id: number; name: string }[];
}) {
  const Icon = ENTITY_ICONS[wf.entityType] || ShieldCheck;
  const accent = ENTITY_ACCENTS[wf.entityType] || "border-gray-200";
  const iconColor = ENTITY_ICON_COLORS[wf.entityType] || "bg-gray-100 text-gray-700";
  const disabled = !local.enabled;

  return (
    <motion.div variants={staggerItem} layout>
      <Card
        className={`relative rounded-xl border shadow-sm transition-all duration-200
          ${disabled ? "opacity-70" : "hover:shadow-md hover:-translate-y-0.5"}
          ${hasChanges ? "ring-2 ring-amber-400/40 ring-offset-2" : ""}
          bg-white`}
      >
        <div className={`absolute inset-0 bg-gradient-to-br ${accent} pointer-events-none`} />
        <div className="relative">
          <CardHeader className="pb-0 px-5 pt-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${iconColor}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-sm font-semibold text-gray-900">
                    {wf.label || ENTITY_LABELS[wf.entityType] || wf.entityType}
                  </CardTitle>
                  <CardDescription className="text-xs leading-relaxed max-w-md">
                    {ENTITY_DESCRIPTIONS[wf.entityType]}
                  </CardDescription>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0 border-gray-200 text-gray-500">
                      {wf.entityType}
                    </Badge>
                    <div className="flex items-center gap-1">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${local.enabled ? "bg-green-500" : "bg-gray-300"}`} />
                      <span className="text-[10px] font-medium text-gray-500">{local.enabled ? "Active" : "Disabled"}</span>
                    </div>
                    {hasChanges && (
                      <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600 bg-amber-50 font-medium">
                        Unsaved
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="px-5 pt-4 pb-4">
            <Separator className="mb-5 bg-gray-100" />

            <div className="flex flex-col lg:flex-row gap-5">
              <div className="flex-1 space-y-5">
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                    <ShieldCheck className="h-3 w-3" />
                    Required Approvals
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={local.requiredApprovals}
                    onChange={(e) => onFieldChange("requiredApprovals", Number(e.target.value))}
                    className="h-9 text-sm font-medium max-w-[140px]"
                  />
                </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                      <Users className="h-3 w-3" />
                      Approver Permissions
                    </label>
                    <PermissionMultiSelect
                      entityType={wf.entityType}
                      value={local.approverPermissionKeys || []}
                      onChange={(keys) => onFieldChange("approverPermissionKeys", keys)}
                    />
                    <p className="text-[10px] text-gray-400">
                      {(local.approverPermissionKeys || []).length > 0
                        ? `${local.approverPermissionKeys.length} permission${local.approverPermissionKeys.length > 1 ? "s" : ""} selected`
                        : "Choose which permissions allow approval"}
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                      <Users className="h-3 w-3" />
                      Designated Roles
                    </label>
                    <RoleMultiSelect
                      roles={roles}
                      value={local.designatedRoleIds || []}
                      onChange={(ids) => onFieldChange("designatedRoleIds", ids)}
                    />
                    <p className="text-[10px] text-gray-400">
                      {(local.designatedRoleIds || []).length > 0
                        ? `${local.designatedRoleIds.length} role${local.designatedRoleIds.length > 1 ? "s" : ""} selected`
                        : "Falls back to permission keys when empty"}
                    </p>
                  </div>
              </div>

              <div className="flex flex-row lg:flex-col gap-3">
                <div className="flex-1 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50/50 px-4 py-3 min-w-[180px]">
                  <div className="flex items-center gap-2.5">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${local.allowAdminOverride ? "bg-amber-100" : "bg-gray-100"}`}>
                      <ShieldCheck className={`h-4 w-4 ${local.allowAdminOverride ? "text-amber-600" : "text-gray-400"}`} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700">Admin Override</p>
                      <p className={`text-[10px] ${local.allowAdminOverride ? "text-amber-600" : "text-gray-400"}`}>
                        {local.allowAdminOverride ? "Override allowed" : "No override"}
                      </p>
                    </div>
                  </div>
                  <ToggleSwitch
                    checked={local.allowAdminOverride}
                    onCheckedChange={(v) => onFieldChange("allowAdminOverride", v)}
                    size="md"
                    color="amber"
                  />
                </div>

                <div className="flex-1 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50/50 px-4 py-3 min-w-[180px]">
                  <div className="flex items-center gap-2.5">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${local.enabled ? "bg-emerald-100" : "bg-red-100"}`}>
                      <Power className={`h-4 w-4 ${local.enabled ? "text-emerald-600" : "text-red-400"}`} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700">Status</p>
                      <p className={`text-[10px] ${local.enabled ? "text-emerald-600" : "text-red-500"}`}>
                        {local.enabled ? "Workflow active" : "Workflow disabled"}
                      </p>
                    </div>
                  </div>
                  <ToggleSwitch
                    checked={local.enabled}
                    onCheckedChange={(v) => onFieldChange("enabled", v)}
                    size="md"
                    color="green"
                  />
                </div>
              </div>
            </div>

            <AnimatePresence>
              {hasChanges && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between"
                >
                  <span className="text-xs text-amber-600 font-medium">You have unsaved changes</span>
                  <div className="flex items-center gap-2">
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => onFieldChange("__reset", true)}
                      className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      Cancel
                    </motion.button>
                    <Button
                      size="sm"
                      onClick={onSave}
                      disabled={isSaving}
                      className="h-8 text-xs bg-[#009A44] hover:bg-[#007a35] text-white rounded-lg shadow-sm shadow-green-200"
                    >
                      {isSaving ? (
                        <LoaderCircle className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Save className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </div>
      </Card>
    </motion.div>
  );
}

export default function ApprovalWorkflowsSettings() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Record<string, Partial<Workflow>>>({});

  const { data, isLoading, error, isError, refetch } = useQuery(["approval-workflows"], async () => {
    const { data } = await api.get("/approval-workflows");
    return data.data as Workflow[];
  });

  const { data: rolesData } = useQuery(["roles"], async () => {
    const { data } = await api.get("/roles");
    return data.data as { id: number; name: string }[];
  });

  const roles = rolesData || [];

  const updateMutation = useMutation(
    async ({ entityType, values }: { entityType: string; values: Partial<Workflow> }) => {
      const { data } = await api.put(`/approval-workflows/${entityType}`, values);
      return data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["approval-workflows"]);
        toast.success("Workflow updated", {
          icon: <CheckCircle className="h-4 w-4 text-green-500" />,
        });
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to update", {
          icon: <XCircle className="h-4 w-4 text-red-500" />,
        });
      },
    }
  );

  const resetMutation = useMutation(
    async () => {
      const { data } = await api.post("/approval-workflows/reset");
      return data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["approval-workflows"]);
        toast.success("Workflows reset to defaults", {
          icon: <RefreshCw className="h-4 w-4 text-green-500" />,
        });
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to reset");
      },
    }
  );

  const workflows = data || [];

  const handleChange = (entityType: string, field: string, value: any) => {
    if (field === "__reset") {
      setEditing((prev) => {
        const next = { ...prev };
        delete next[entityType];
        return next;
      });
      return;
    }
    setEditing((prev) => ({
      ...prev,
      [entityType]: { ...prev[entityType], [field]: value },
    }));
  };

  const handleSave = (entityType: string) => {
    const changes = editing[entityType];
    if (!changes) return;
    updateMutation.mutate({ entityType, values: changes });
    setEditing((prev) => {
      const next = { ...prev };
      delete next[entityType];
      return next;
    });
  };

  const enabledCount = workflows.filter((w) => w.enabled).length;
  const editedCount = Object.keys(editing).length;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-5xl">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-[#009A44] to-[#007a35] text-white shadow-sm">
              <Settings2 className="h-4 w-4" />
            </div>
            Approval Workflows
          </h1>
          <p className="text-sm text-gray-500 mt-1.5 ml-10">
            Configure how many approvals are required per entity type and who can approve
          </p>
        </div>
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => resetMutation.mutate()}
            disabled={resetMutation.isLoading}
            className="h-9 text-sm rounded-lg border-gray-200 hover:bg-gray-50 hover:border-gray-300"
          >
            {resetMutation.isLoading ? (
              <LoaderCircle className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            )}
            Reset to Defaults
          </Button>
        </motion.div>
      </motion.div>

      {workflows.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-3 flex-wrap"
        >
          <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
            <span className="font-semibold text-gray-700">{workflows.length}</span> total
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            <span className="font-semibold text-green-700">{enabledCount}</span> active
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
            <span className="font-semibold text-gray-700">{workflows.length - enabledCount}</span> disabled
          </div>
          {editedCount > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
              <Sparkles className="h-3 w-3" />
              <span className="font-semibold">{editedCount}</span> pending
            </div>
          )}
        </motion.div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      ) : isError ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card className="border-red-200 bg-gradient-to-br from-red-50 to-red-50/50 rounded-xl p-8 text-center">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mx-auto mb-4">
              <AlertTriangle className="h-7 w-7 text-red-400" />
            </div>
            <p className="text-sm text-red-600 font-semibold">Failed to load workflows</p>
            <p className="text-xs text-red-500 mt-1 max-w-sm mx-auto">
              {(error as any)?.message || "The approval_workflows table may not exist yet. Run migrations or reset to defaults."}
            </p>
            <div className="flex items-center justify-center gap-3 mt-5">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button variant="outline" size="sm" onClick={() => refetch()} className="h-9 text-xs rounded-lg border-red-200 hover:bg-red-50">
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => resetMutation.mutate()}
                  disabled={resetMutation.isLoading}
                  className="h-9 text-xs rounded-lg"
                >
                  {resetMutation.isLoading && <LoaderCircle className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset to Defaults
                </Button>
              </motion.div>
            </div>
          </Card>
        </motion.div>
      ) : workflows.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card className="border-dashed border-gray-300 rounded-xl p-10 text-center bg-gray-50/50">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mx-auto mb-4">
              <ShieldCheck className="h-8 w-8 text-gray-300" />
            </div>
            <p className="text-sm text-gray-500 font-semibold">No approval workflows configured</p>
            <p className="text-xs text-gray-400 mt-1.5">
              Click the button below to seed the initial configuration
            </p>
            <motion.div className="mt-5" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                size="sm"
                onClick={() => resetMutation.mutate()}
                disabled={resetMutation.isLoading}
                className="h-9 text-sm bg-[#009A44] hover:bg-[#007a35] text-white rounded-lg shadow-sm"
              >
                {resetMutation.isLoading && <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />}
                <RotateCcw className="h-4 w-4 mr-2" />
                Seed Defaults
              </Button>
            </motion.div>
          </Card>
        </motion.div>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="show"
          className="space-y-4"
        >
          {workflows.map((wf) => {
            const hasChanges = !!editing[wf.entityType];
            const local = { ...wf, ...(editing[wf.entityType] || {}) };
            return (
              <WorkflowCard
                key={wf.id}
                wf={wf}
                editing={editing[wf.entityType]}
                hasChanges={hasChanges}
                local={local}
                onFieldChange={(field, value) => handleChange(wf.entityType, field, value)}
                onSave={() => handleSave(wf.entityType)}
                isSaving={updateMutation.isLoading}
                roles={roles}
              />
            );
          })}
        </motion.div>
      )}
    </div>
  );
}
